import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { bookingService } from '../../services/bookingService';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { formatNumber } from '../../utils/formatNumber';

const DashboardHome = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    upcomingBookings: 0,
    pendingRequests: 0,
    completedBookings: 0,
    cancelledBookings: 0,
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [accountStatus, setAccountStatus] = useState({
    emailVerified: false,
    phoneVerified: false,
  });

  useEffect(() => {
    loadDashboardData();
    checkAccountStatus();
  }, [userProfile]);

  const checkAccountStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setAccountStatus({
          emailVerified: user.email_confirmed_at !== null,
          phoneVerified: user.phone_confirmed_at !== null || user.phone !== null,
        });
      }
    } catch (error) {
      console.error('Error checking account status:', error);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Load bookings
      const bookings = await bookingService.getMyBookings('customer');

      // Calculate stats with new booking statuses
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      const upcoming = bookings.filter((b) => {
        const status = b.status;
        if (status === 'session_started' || status === 'awaiting_delivery') return true;
        if (status === 'paid_confirmed' || status === 'confirmed') {
          const d = new Date(b.scheduledAt || b.bookingDate || b.booking_date);
          return !isNaN(d.getTime()) && d >= startOfToday;
        }
        return false;
      });
      const pending = bookings.filter((b) => 
        b.status === 'pending_photographer_confirmation' || 
        b.status === 'pending' || 
        b.status === 'awaiting_payment'
      );
      const completed = bookings.filter((b) => b.status === 'completed');
      const cancelled = bookings.filter((b) => 
        b.status === 'cancelled_by_customer' || 
        b.status === 'cancelled_by_admin' || 
        b.status === 'cancelled' ||
        b.status === 'cancelled_by_photographer' ||
        b.status === 'cancellation_requested' ||
        b.status === 'declined' ||
        b.status === 'expired'
      );

      setStats({
        upcomingBookings: upcoming.length,
        pendingRequests: pending.length,
        completedBookings: completed.length,
        cancelledBookings: cancelled.length,
      });

      // Recent activity (last 5 bookings)
      const recent = bookings
        .sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt))
        .slice(0, 5)
        .map((booking) => ({
          id: booking.id,
          type: booking.status === 'pending' ? 'request_sent' : booking.status === 'confirmed' ? 'booking_confirmed' : booking.status === 'completed' ? 'booking_completed' : 'booking_cancelled',
          message: getActivityMessage(booking),
          date: booking.created_at || booking.createdAt,
          bookingId: booking.id,
        }));

      setRecentActivity(recent);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityMessage = (booking) => {
    const photographerName = booking.photographer?.full_name || booking.photographer?.fullName || 'مصور';
    const status = booking.status;
    
    if (status === 'pending_photographer_confirmation' || status === 'pending') {
      return `تم إرسال طلب حجز إلى ${photographerName || 'مصور'}`;
    }
    if (status === 'awaiting_payment') {
      return `في انتظار إتمام الدفع لحجزك مع ${photographerName || 'مصور'}`;
    }
    if (status === 'paid_confirmed' || status === 'confirmed') {
      return `تم تأكيد حجزك ودفعه مع ${photographerName || 'مصور'}`;
    }
    if (status === 'session_started') {
      return `جلستك مع ${photographerName || 'مصور'} جارية الآن`;
    }
    if (status === 'awaiting_delivery') {
      return `${photographerName || 'المصور'} يجهّز صورك`;
    }
    if (status === 'completed') {
      return `اكتمل حجزك مع ${photographerName || 'مصور'}`;
    }
    if (status === 'cancelled_by_customer' || status === 'cancelled') {
      return `تم إلغاء حجزك مع ${photographerName || 'مصور'}`;
    }
    if (status === 'cancelled_by_photographer') {
      return `اعتذر المصور عن حجزك — يمكنك اختيار مصور آخر أو طلب استرداد`;
    }
    if (status === 'cancellation_requested') {
      return `طلب إلغاء حجزك قيد المراجعة`;
    }
    if (status === 'declined') {
      return `تم رفض حجزك من قبل ${photographerName || 'مصور'}`;
    }
    if (status === 'expired') {
      return `انتهت صلاحية حجزك`;
    }
    
    return 'تم تحديث الحجز';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Welcome Header */}
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 mb-2 break-words">
              حياك الله، {userProfile?.full_name || 'مستخدم'}
            </h1>
            <p className="text-xs sm:text-sm md:text-base text-slate-600">إليك نظرة عامة على حسابك وحجوزاتك</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2 sm:px-3 py-1 bg-success/10 text-success rounded-full text-xs sm:text-sm font-medium flex items-center gap-1 whitespace-nowrap">
              <Icon name="CheckCircle" size={14} className="sm:w-4 sm:h-4" />
              الحساب نشط
            </span>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="p-2 sm:p-3 bg-primary/10 rounded-lg sm:rounded-xl">
              <Icon name="Calendar" size={20} className="sm:w-6 sm:h-6 text-primary" />
            </div>
          </div>
          <h3 className="text-xs sm:text-sm text-slate-600 mb-1">الحجوزات القادمة</h3>
          <p className="text-2xl sm:text-3xl font-bold text-slate-900">{stats.upcomingBookings}</p>
        </div>

        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="p-2 sm:p-3 bg-warning/10 rounded-lg sm:rounded-xl">
              <Icon name="Clock" size={20} className="sm:w-6 sm:h-6 text-warning" />
            </div>
          </div>
          <h3 className="text-xs sm:text-sm text-slate-600 mb-1">طلبات معلقة</h3>
          <p className="text-2xl sm:text-3xl font-bold text-slate-900">{stats.pendingRequests}</p>
        </div>

        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="p-2 sm:p-3 bg-success/10 rounded-lg sm:rounded-xl">
              <Icon name="CheckCircle" size={20} className="sm:w-6 sm:h-6 text-success" />
            </div>
          </div>
          <h3 className="text-xs sm:text-sm text-slate-600 mb-1">الحجوزات المكتملة</h3>
          <p className="text-2xl sm:text-3xl font-bold text-slate-900">{stats.completedBookings}</p>
        </div>

        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="p-2 sm:p-3 bg-error/10 rounded-lg sm:rounded-xl">
              <Icon name="XCircle" size={20} className="sm:w-6 sm:h-6 text-error" />
            </div>
          </div>
          <h3 className="text-xs sm:text-sm text-slate-600 mb-1">الحجوزات الملغاة</h3>
          <p className="text-2xl sm:text-3xl font-bold text-slate-900">{stats.cancelledBookings}</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-3 sm:mb-4">الإجراءات السريعة</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <Button
            variant="default"
            onClick={() => navigate('/booking-wizard')}
            iconName="Plus"
            iconPosition="right"
            className="justify-start"
          >
            حجز جديد
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/customer-dashboard/bookings')}
            iconName="Calendar"
            iconPosition="right"
            className="justify-start"
          >
            عرض جميع الحجوزات
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/customer-dashboard/profile')}
            iconName="User"
            iconPosition="right"
            className="justify-start"
          >
            تحديث الملف الشخصي
          </Button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-3 sm:mb-4">النشاط الأخير</h2>
        {recentActivity.length > 0 ? (
          <div className="space-y-3 sm:space-y-4">
            {recentActivity.map((activity) => (
              <div
                key={activity.id}
                className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                onClick={() => navigate(`/customer-dashboard/bookings?bookingId=${activity.bookingId}`)}
              >
                <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg flex-shrink-0">
                  <Icon
                    name={
                      activity.type === 'request_sent'
                        ? 'Send'
                        : activity.type === 'booking_confirmed'
                        ? 'CheckCircle'
                        : activity.type === 'booking_completed'
                        ? 'CheckCircle2'
                        : 'XCircle'
                    }
                    size={18}
                    className="sm:w-5 sm:h-5 text-primary"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-slate-900 break-words">{activity.message}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {new Date(activity.date).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 sm:py-12">
            <Icon name="Activity" size={40} className="sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 text-slate-400" />
            <p className="text-sm sm:text-base text-slate-600 mb-3 sm:mb-4">لا يوجد نشاط حديث</p>
            <Button
              variant="outline"
              onClick={() => navigate('/booking-wizard')}
              iconName="Plus"
              className="text-sm sm:text-base"
            >
              ابدأ حجز جديد
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardHome;
